import React, { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ListChecks,
  Loader2,
  CalendarDays,
  AlertCircle,
  Video,
  Lock,
  Pin,
  PinOff,
  StickyNote,
  GripVertical,
  X,
  ExternalLink,
  LayoutGrid,
  List,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";
import { Button, ModalShell } from "../../components/shared";
import { useVendorBookings } from "../../services/vendorBookingService";
import { vendorPrivateBookingApi } from "../../services/vendorPrivateBookingService";
import {
  getConsultationCalendarLeads,
  updateLeadSchedule,
  updateLeadChecklist,
  addLeadNote,
} from "../../services/consultationService";
import {
  getExternalLeadCalendarLeads,
  vendorExternalLeadApi,
} from "../../services/vendorExternalLeadService";
import { useNavigate } from "react-router-dom";
import {
  VendorPageHeader,
  VendorGuideCallout,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";
import { CHANNEL, getBookingChannel, isPrivateBooking } from "../../utils/bookingSource";
import { useToastContext } from "../../contexts/ToastContext";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "internal", label: "Internal" },
  { key: "external", label: "External" },
  { key: "leads", label: "Leads" },
];

const EXTERNAL_BOOKING_CHIP =
  "bg-teal-50 text-teal-950 border-teal-300 border-l-4 border-l-teal-600";
const INTERNAL_LEAD_CHIP =
  "bg-orange-50 text-orange-950 border-orange-300 border-l-4 border-l-orange-500";
const EXTERNAL_LEAD_CHIP =
  "bg-amber-50 text-amber-950 border-amber-300 border-l-4 border-l-amber-600";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseLocalYmd = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatTime = (timeString) => {
  if (!timeString) return "";
  if (/^\d{1,2}:\d{2}/.test(String(timeString))) {
    const [hRaw, mRaw] = String(timeString).split(":");
    const h = Number(hRaw);
    const m = Number(mRaw);
    if (Number.isNaN(h)) return timeString;
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
  }
  const date = new Date(timeString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const eventDragId = (event) => {
  if (event.kind === "lead") {
    return `lead:${event.lead_channel || event.channel || "internal"}:${event.id}`;
  }
  return `booking:${event.id}`;
};

const parseDragId = (id) => {
  const parts = String(id).split(":");
  if (parts[0] === "lead") {
    return { kind: "lead", channel: parts[1], id: parts.slice(2).join(":") };
  }
  if (parts[0] === "booking") {
    return { kind: "booking", id: parts.slice(1).join(":") };
  }
  return null;
};

const leadChipClass = (event) =>
  (event.lead_channel || event.channel) === "external"
    ? EXTERNAL_LEAD_CHIP
    : INTERNAL_LEAD_CHIP;

const leadChannelLabel = (event) =>
  (event.lead_channel || event.channel) === "external" ? "Ext lead" : "Int lead";

function DayDropZone({ dayKey, children, className = "" }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${dayKey}` });
  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? "bg-ink/[0.04] ring-2 ring-inset ring-ink/20" : ""}`}
    >
      {children}
    </div>
  );
}

function DraggableEventCard({
  event,
  children,
  disabled = false,
  className = "",
  onOpen,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: eventDragId(event),
    disabled,
    data: { event },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${className} ${isDragging ? "opacity-40" : ""} ${
        disabled ? "" : "touch-manipulation"
      }`}
    >
      <div className="flex items-stretch gap-0.5">
        {!disabled && (
          <button
            type="button"
            className="shrink-0 self-center p-0.5 text-ink-tertiary hover:text-ink cursor-grab active:cursor-grabbing"
            aria-label="Drag to reschedule"
            {...listeners}
            {...attributes}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => onOpen?.(event)}
        >
          {children}
        </button>
      </div>
    </div>
  );
}

const VendorCalendar = () => {
  const navigate = useNavigate();
  const toast = useToastContext();
  const queryClient = useQueryClient();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventFilter, setEventFilter] = useState("all");
  const [viewMode, setViewMode] = useState("month"); // month | agenda
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [draggingEvent, setDraggingEvent] = useState(null);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const monthStart = new Date(currentYear, currentMonth, 1);
  const monthEnd = new Date(currentYear, currentMonth + 1, 0);
  const startDate = toYmd(monthStart);
  const endDate = toYmd(monthEnd);

  const calendarParams = {
    calendar: 1,
    start_date: startDate,
    end_date: endDate,
    per_page: 500,
  };

  const {
    data: bookingsResponse,
    isLoading: bookingsLoading,
    error: bookingsError,
  } = useVendorBookings(calendarParams);

  const allBookings = useMemo(
    () => bookingsResponse?.data?.data || [],
    [bookingsResponse?.data?.data],
  );

  const {
    data: internalLeadsResponse,
    isLoading: internalLeadsLoading,
    error: internalLeadsError,
  } = useQuery({
    queryKey: ["consultationCalendarLeads", startDate, endDate],
    queryFn: () =>
      getConsultationCalendarLeads({ start_date: startDate, end_date: endDate }),
    staleTime: 60 * 1000,
  });

  const {
    data: externalLeadsResponse,
    isLoading: externalLeadsLoading,
    error: externalLeadsError,
  } = useQuery({
    queryKey: ["vendorExternalLeadCalendar", startDate, endDate],
    queryFn: () =>
      getExternalLeadCalendarLeads({ start_date: startDate, end_date: endDate }),
    staleTime: 60 * 1000,
  });

  const internalLeadEvents = useMemo(
    () =>
      (internalLeadsResponse?.data || []).map((lead) => ({
        ...lead,
        kind: "lead",
        lead_channel: "internal",
        channel: "internal",
      })),
    [internalLeadsResponse?.data],
  );

  const externalLeadEvents = useMemo(
    () =>
      (externalLeadsResponse?.data || []).map((lead) => ({
        ...lead,
        kind: "lead",
        lead_channel: "external",
        channel: "external",
      })),
    [externalLeadsResponse?.data],
  );

  const allLeadEvents = useMemo(
    () => [...internalLeadEvents, ...externalLeadEvents],
    [internalLeadEvents, externalLeadEvents],
  );

  const internalBookingCount = useMemo(
    () =>
      allBookings.filter((b) => getBookingChannel(b) === CHANNEL.INTERNAL).length,
    [allBookings],
  );
  const externalBookingCount = useMemo(
    () =>
      allBookings.filter((b) => getBookingChannel(b) === CHANNEL.EXTERNAL).length,
    [allBookings],
  );

  const bookings = useMemo(() => {
    if (eventFilter === "internal") {
      return allBookings.filter((b) => getBookingChannel(b) === CHANNEL.INTERNAL);
    }
    if (eventFilter === "external") {
      return allBookings.filter((b) => getBookingChannel(b) === CHANNEL.EXTERNAL);
    }
    if (eventFilter === "leads") return [];
    return allBookings;
  }, [allBookings, eventFilter]);

  const leadEvents = useMemo(() => {
    if (eventFilter === "internal" || eventFilter === "external") return [];
    return allLeadEvents;
  }, [allLeadEvents, eventFilter]);

  const isLoading =
    bookingsLoading || internalLeadsLoading || externalLeadsLoading;
  const error = bookingsError || internalLeadsError || externalLeadsError;

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateKey = toYmd(date);

      const dayBookings = bookings
        .filter((booking) => {
          const bookingDate = parseLocalYmd(
            String(booking.event_date || "").slice(0, 10),
          );
          return bookingDate && toYmd(bookingDate) === dateKey;
        })
        .map((booking) => ({ ...booking, kind: "booking" }));

      const dayLeads = leadEvents
        .filter((lead) => {
          const ymd = lead.calendar_date || lead.scheduled_date || lead.event_date;
          return ymd && String(ymd).slice(0, 10) === dateKey;
        })
        .map((lead) => ({ ...lead, kind: "lead" }))
        .sort((a, b) => Number(Boolean(b.pinned)) - Number(Boolean(a.pinned)));

      const events = [...dayLeads, ...dayBookings];
      days.push({ day, date, dateKey, bookings: dayBookings, leads: dayLeads, events });
    }
    return days;
  }, [
    currentMonth,
    currentYear,
    bookings,
    leadEvents,
    daysInMonth,
    startingDayOfWeek,
  ]);

  const agendaEvents = useMemo(() => {
    const rows = [];
    calendarDays.forEach((dayData) => {
      if (!dayData) return;
      dayData.events.forEach((event) => {
        rows.push({
          ...event,
          agendaDate: dayData.date,
          agendaDateKey: dayData.dateKey,
        });
      });
    });
    return rows.sort((a, b) => {
      const pin = Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
      if (pin) return pin;
      return String(a.agendaDateKey).localeCompare(String(b.agendaDateKey));
    });
  }, [calendarDays]);

  const invalidateCalendar = () => {
    queryClient.invalidateQueries({ queryKey: ["vendorBookings"] });
    queryClient.invalidateQueries({ queryKey: ["consultationCalendarLeads"] });
    queryClient.invalidateQueries({ queryKey: ["vendorExternalLeadCalendar"] });
    queryClient.invalidateQueries({ queryKey: ["pendingConsultationLeadCount"] });
    queryClient.invalidateQueries({ queryKey: ["pendingExternalLeadCount"] });
  };

  const moveMutation = useMutation({
    mutationFn: async ({ event, newDate }) => {
      if (event.kind === "lead") {
        const channel = event.lead_channel || event.channel;
        const time =
          (event.scheduled_time || event.event_time || "").toString().slice(0, 5) ||
          undefined;
        if (channel === "external") {
          const payload =
            event.date_field === "event_date" && !event.scheduled_date
              ? { event_date: newDate, ...(time ? { event_time: time } : {}) }
              : {
                  scheduled_date: newDate,
                  ...(time ? { scheduled_time: time } : {}),
                };
          return vendorExternalLeadApi.updateLead(event.id, payload);
        }
        return updateLeadSchedule(event.id, {
          scheduled_date: newDate,
          ...(time ? { scheduled_time: time } : {}),
        });
      }

      if (isPrivateBooking(event)) {
        return vendorPrivateBookingApi.update({
          id: event.id,
          data: { event_date: newDate },
        });
      }

      throw new Error("Internal marketplace bookings can’t be dragged — open the booking to request a date change.");
    },
    onSuccess: () => {
      toast.success("Moved on calendar");
      invalidateCalendar();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Could not move this item. Try again.",
      );
    },
  });

  const pinMutation = useMutation({
    mutationFn: async ({ event, pinned }) => {
      const channel = event.lead_channel || event.channel;
      if (channel === "external") {
        return vendorExternalLeadApi.updateChecklist(event.id, {
          checklist: { added_to_calendar: pinned },
        });
      }
      return updateLeadChecklist(event.id, { added_to_calendar: pinned });
    },
    onSuccess: (_, vars) => {
      toast.success(vars.pinned ? "Pinned to calendar" : "Unpinned");
      invalidateCalendar();
      setActiveEvent((prev) =>
        prev && prev.id === vars.event.id ? { ...prev, pinned: vars.pinned } : prev,
      );
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Could not update pin");
    },
  });

  const noteMutation = useMutation({
    mutationFn: async ({ event, note }) => {
      const channel = event.lead_channel || event.channel;
      if (channel === "external") {
        const stamp = new Date().toLocaleString();
        const prev = (event.vendor_notes || "").trim();
        const next = prev ? `${prev}\n\n[${stamp}] ${note}` : `[${stamp}] ${note}`;
        return vendorExternalLeadApi.updateLead(event.id, { vendor_notes: next });
      }
      return addLeadNote(event.id, note);
    },
    onSuccess: () => {
      toast.success("Note saved");
      setNoteDraft("");
      invalidateCalendar();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Could not save note");
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
  );

  const canDragEvent = (event) => {
    if (event.kind === "lead") return event.status !== "booked";
    // Marketplace bookings need the formal date-change flow; only External (manual) can drag.
    return isPrivateBooking(event);
  };

  const handleDragStart = (e) => {
    setDraggingEvent(e.active.data.current?.event || null);
  };

  const handleDragEnd = (e) => {
    setDraggingEvent(null);
    const { active, over } = e;
    if (!over) return;
    const overId = String(over.id);
    if (!overId.startsWith("day:")) return;
    const newDate = overId.slice(4);
    const parsed = parseDragId(active.id);
    if (!parsed) return;
    const event = active.data.current?.event;
    if (!event) return;
    const current =
      event.kind === "lead"
        ? event.calendar_date || event.scheduled_date || event.event_date
        : String(event.event_date || "").slice(0, 10);
    if (current === newDate) return;
    moveMutation.mutate({ event, newDate });
  };

  const goToPreviousMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  const goToNextMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDayKey(toYmd(today));
  };

  const getStatusColor = (status, booking = null) => {
    if (booking && isPrivateBooking(booking)) return EXTERNAL_BOOKING_CHIP;
    const colors = {
      pending: "bg-amber-50 text-amber-800 border-amber-200",
      confirmed: "bg-surface-muted text-ink border-border",
      in_progress: "bg-surface-muted text-ink border-border",
      completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
      cancelled: "bg-red-50 text-red-700 border-red-200",
      no_show: "bg-gray-100 text-gray-700 border-gray-200",
    };
    return colors[status] || colors.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pending",
      confirmed: "Confirmed",
      in_progress: "In Progress",
      completed: "Completed",
      cancelled: "Cancelled",
      no_show: "No Show",
    };
    return labels[status] || status || "Pending";
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const visibleBookingCount = bookings.length;
  const visibleLeadCount = leadEvents.length;
  const totalEvents = visibleBookingCount + visibleLeadCount;

  const openEvent = (event) => {
    setActiveEvent(event);
    setNoteDraft("");
  };

  const openFullPage = (event) => {
    if (event.kind === "lead") {
      const channel = event.lead_channel || event.channel;
      if (channel === "external") {
        navigate(`/vendor/external-leads?lead=${event.id}`);
      } else {
        navigate(
          `/vendor/consultations?status=${encodeURIComponent(
            event.status || "pending",
          )}&lead=${event.id}`,
        );
      }
      return;
    }
    navigate(`/vendor/bookings/${event.id}`);
  };

  const selectedDayData = useMemo(
    () => calendarDays.find((d) => d && d.dateKey === selectedDayKey) || null,
    [calendarDays, selectedDayKey],
  );

  const filterCount = (key) => {
    if (key === "internal") return internalBookingCount;
    if (key === "external") return externalBookingCount;
    if (key === "leads") return allLeadEvents.length;
    return null;
  };

  const renderEventBody = (event, compact = false) => {
    if (event.kind === "lead") {
      return (
        <>
          <div className="flex items-center justify-between gap-1 mb-0.5">
            <div className="flex items-center gap-1 min-w-0">
              {event.pinned ? (
                <Pin className="w-3 h-3 shrink-0 text-orange-700" />
              ) : (
                <Video className="w-3 h-3 shrink-0 text-orange-600" />
              )}
              <span className="font-medium truncate">
                {formatTime(
                  event.scheduled_time || event.event_time || event.start_time,
                )}
              </span>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wide shrink-0 text-orange-800 bg-orange-100/80 px-1.5 py-0.5 rounded">
              {leadChannelLabel(event)}
            </span>
          </div>
          <div className="truncate text-[11px] font-semibold">
            {event.title ||
              event.calendar_title ||
              event.client_display_name ||
              "Lead"}
          </div>
          {!compact && (
            <div className="truncate text-[10px] opacity-80">
              {event.client_display_name || ""}
              {event.status_label
                ? `${event.client_display_name ? " · " : ""}${event.status_label}`
                : ""}
            </div>
          )}
        </>
      );
    }

    return (
      <>
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <div className="flex items-center gap-1 min-w-0">
            {isPrivateBooking(event) ? (
              <Lock className="w-3 h-3 shrink-0 text-teal-700" />
            ) : (
              <Clock className="w-3 h-3 shrink-0 opacity-70" />
            )}
            <span className="font-medium truncate">
              {formatTime(event.start_time)}
            </span>
          </div>
          {isPrivateBooking(event) ? (
            <span className="text-[10px] font-bold uppercase tracking-wide shrink-0 text-teal-800 bg-teal-100 px-1.5 py-0.5 rounded">
              External
            </span>
          ) : (
            <span className="text-[10px] font-semibold uppercase tracking-wide shrink-0 opacity-80">
              {getStatusLabel(event.status)}
            </span>
          )}
        </div>
        <div className="font-mono text-[11px] font-medium truncate">
          #{event.booking_reference}
        </div>
        {!compact && (
          <div className="truncate text-[11px] opacity-90">
            {event.client_display_name ||
              event.guest_name ||
              event.client?.name ||
              "Client"}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Calendar"
        subtitle="Bookings plus Internal and External client leads — drag to reschedule, pin, and add notes."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-xl border border-border bg-gray-100 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("month")}
                className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium ${
                  viewMode === "month"
                    ? "bg-surface text-ink shadow-sm"
                    : "text-ink-secondary"
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Month
              </button>
              <button
                type="button"
                onClick={() => setViewMode("agenda")}
                className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium ${
                  viewMode === "agenda"
                    ? "bg-surface text-ink shadow-sm"
                    : "text-ink-secondary"
                }`}
              >
                <List className="h-3.5 w-3.5" />
                Agenda
              </button>
            </div>
            <Button variant="outline" onClick={goToToday} size="sm" className="rounded-md">
              Today
            </Button>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={goToPreviousMonth}
                aria-label="Previous month"
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-surface text-ink-secondary hover:bg-surface-muted hover:border-ink transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={goToNextMonth}
                aria-label="Next month"
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-surface text-ink-secondary hover:bg-surface-muted hover:border-ink transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.calendar.title}
        steps={VENDOR_PAGE_GUIDES.calendar.steps}
        icon={ListChecks}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDraggingEvent(null)}
      >
        <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-border px-4 py-3.5 sm:px-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-lg sm:text-xl font-semibold text-ink tracking-tight">
                {MONTH_NAMES[currentMonth]} {currentYear}
              </h2>
              <p className="text-xs sm:text-sm text-ink-tertiary">
                {isLoading
                  ? "Loading events…"
                  : error
                    ? "Unable to load events"
                    : `${totalEvents} shown · ${internalBookingCount} internal · ${externalBookingCount} external · ${allLeadEvents.length} leads`}
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="inline-flex max-w-full flex-wrap rounded-xl bg-gray-100 p-1 border border-border w-fit">
                {FILTERS.map((f) => {
                  const active = eventFilter === f.key;
                  const count = filterCount(f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setEventFilter(f.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        active
                          ? "bg-surface text-ink shadow-sm border border-border"
                          : "text-ink-secondary hover:text-ink"
                      }`}
                    >
                      {f.label}
                      {count != null && !isLoading ? ` (${count})` : ""}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-ink-secondary">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-ink" />
                  Internal booking
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-teal-600" />
                  External booking
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-orange-500" />
                  Internal lead
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />
                  External lead
                </span>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center px-4">
              <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
              <p className="text-sm text-ink-tertiary">Loading calendar…</p>
            </div>
          ) : error ? (
            <div className="py-16 sm:py-20 text-center px-4">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-500">
                <AlertCircle className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-ink">Error loading calendar</h3>
              <p className="mt-1.5 text-sm text-ink-tertiary max-w-md mx-auto leading-relaxed">
                Unable to load your calendar. Please try again shortly.
              </p>
            </div>
          ) : viewMode === "agenda" ? (
            <div className="divide-y divide-border">
              {agendaEvents.length === 0 ? (
                <div className="py-12 text-center px-4">
                  <CalendarDays className="mx-auto mb-3 h-8 w-8 text-ink-tertiary" />
                  <p className="text-sm font-medium text-ink">Nothing in this month</p>
                  <p className="mt-1 text-xs text-ink-tertiary">
                    Client leads with a preferred or meeting date appear here automatically.
                  </p>
                </div>
              ) : (
                agendaEvents.map((event) => (
                  <div
                    key={`${event.kind}-${event.lead_channel || "b"}-${event.id}-${event.agendaDateKey}`}
                    className="flex items-start gap-3 px-4 py-3 sm:px-5 hover:bg-surface-muted/60"
                  >
                    <div className="w-16 shrink-0 pt-0.5 text-right">
                      <p className="text-xs font-semibold text-ink">
                        {event.agendaDate.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                      <p className="text-[10px] text-ink-tertiary">
                        {event.agendaDate.toLocaleDateString(undefined, {
                          weekday: "short",
                        })}
                      </p>
                    </div>
                    <DayDropZone dayKey={event.agendaDateKey} className="min-w-0 flex-1">
                      <DraggableEventCard
                        event={event}
                        disabled={!canDragEvent(event)}
                        onOpen={openEvent}
                        className={`rounded-lg border p-2.5 ${
                          event.kind === "lead"
                            ? leadChipClass(event)
                            : getStatusColor(event.status, event)
                        }`}
                      >
                        {renderEventBody(event)}
                      </DraggableEventCard>
                    </DayDropZone>
                  </div>
                ))
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 border-b border-border bg-surface-muted/80">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                  <div
                    key={day}
                    className="px-1 py-2.5 sm:px-2 sm:py-3 text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-ink-tertiary"
                  >
                    <span className="sm:hidden">{day.slice(0, 1)}</span>
                    <span className="hidden sm:inline">{day}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {calendarDays.map((dayData, index) => {
                  if (!dayData) {
                    return (
                      <div
                        key={`empty-${index}`}
                        className={`min-h-[72px] sm:min-h-[110px] border-r border-b border-border bg-surface-muted/60 ${(index + 1) % 7 === 0 ? "border-r-0" : ""}`}
                      />
                    );
                  }
                  const today = isToday(dayData.date);
                  return (
                    <DayDropZone
                      key={dayData.dateKey}
                      dayKey={dayData.dateKey}
                      className={`relative min-h-[72px] sm:min-h-[110px] border-r border-b border-border p-1 sm:p-2 overflow-hidden transition-colors ${
                        selectedDayKey === dayData.dateKey
                          ? "bg-surface-muted"
                          : "bg-surface hover:bg-surface-muted/50"
                      } ${(index + 1) % 7 === 0 ? "border-r-0" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedDayKey(dayData.dateKey)}
                        className="mb-1 sm:mb-2"
                      >
                        <span
                          className={`text-xs sm:text-sm font-medium w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full ${
                            today
                              ? "bg-ink text-white ring-2 ring-ink/20"
                              : "text-ink"
                          }`}
                        >
                          {dayData.day}
                        </span>
                      </button>

                      <div className="space-y-1">
                        {dayData.events.slice(0, 3).map((event, eventIndex) => (
                          <DraggableEventCard
                            key={eventDragId(event)}
                            event={event}
                            disabled={!canDragEvent(event)}
                            onOpen={openEvent}
                            className={`text-xs p-1 sm:p-1.5 rounded-md border hover:brightness-[0.98] transition-all ${
                              eventIndex >= 2 ? "hidden sm:block" : ""
                            } ${
                              event.kind === "lead"
                                ? leadChipClass(event)
                                : getStatusColor(event.status, event)
                            }`}
                          >
                            {renderEventBody(event, true)}
                          </DraggableEventCard>
                        ))}
                        {dayData.events.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setSelectedDayKey(dayData.dateKey)}
                            className="w-full text-[10px] sm:text-[11px] text-ink-tertiary text-center py-0.5 font-medium hover:text-ink sm:hidden"
                          >
                            +{dayData.events.length - 2} more
                          </button>
                        )}
                        {dayData.events.length > 3 && (
                          <button
                            type="button"
                            onClick={() => setSelectedDayKey(dayData.dateKey)}
                            className="hidden w-full text-[11px] text-ink-tertiary text-center py-0.5 font-medium hover:text-ink sm:block"
                          >
                            +{dayData.events.length - 3} more
                          </button>
                        )}
                      </div>
                    </DayDropZone>
                  );
                })}
              </div>

              {!isLoading && !error && totalEvents === 0 && (
                <div className="border-t border-border py-10 text-center px-4">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
                    <CalendarDays className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-semibold text-ink">No events this month</h3>
                  <p className="mt-1 text-sm text-ink-tertiary max-w-sm mx-auto leading-relaxed">
                    {eventFilter === "leads"
                      ? "No Internal or External client leads with a date this month yet."
                      : eventFilter === "external"
                        ? `No External bookings for ${MONTH_NAMES[currentMonth]} yet.`
                        : eventFilter === "internal"
                          ? `No Internal bookings for ${MONTH_NAMES[currentMonth]} yet.`
                          : "Bookings and client leads with preferred or meeting dates show here."}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {selectedDayData && (
          <div className="mt-4 rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold text-ink">
                {selectedDayData.date.toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedDayKey(null)}
                className="rounded-md p-1.5 text-ink-tertiary hover:bg-surface-muted hover:text-ink"
                aria-label="Close day panel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {selectedDayData.events.length === 0 ? (
              <p className="text-sm text-ink-tertiary">No events on this day. Drop an item here to reschedule.</p>
            ) : (
              <div className="space-y-2">
                {selectedDayData.events.map((event) => (
                  <DraggableEventCard
                    key={`panel-${eventDragId(event)}`}
                    event={event}
                    disabled={!canDragEvent(event)}
                    onOpen={openEvent}
                    className={`rounded-lg border p-3 ${
                      event.kind === "lead"
                        ? leadChipClass(event)
                        : getStatusColor(event.status, event)
                    }`}
                  >
                    {renderEventBody(event)}
                  </DraggableEventCard>
                ))}
              </div>
            )}
          </div>
        )}

        <DragOverlay>
          {draggingEvent ? (
            <div
              className={`w-56 rounded-lg border p-2 shadow-lg text-xs ${
                draggingEvent.kind === "lead"
                  ? leadChipClass(draggingEvent)
                  : getStatusColor(draggingEvent.status, draggingEvent)
              }`}
            >
              {renderEventBody(draggingEvent, true)}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <ModalShell
        isOpen={Boolean(activeEvent)}
        onClose={() => setActiveEvent(null)}
        panelClassName="max-w-md overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
      >
        {activeEvent && (
          <div className="flex max-h-[min(88dvh,640px)] flex-col">
            <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3.5">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  {activeEvent.kind === "lead"
                    ? leadChannelLabel(activeEvent)
                    : isPrivateBooking(activeEvent)
                      ? "External booking"
                      : "Internal booking"}
                </p>
                <h3 className="mt-0.5 truncate text-base font-semibold text-ink">
                  {activeEvent.kind === "lead"
                    ? activeEvent.title ||
                      activeEvent.client_display_name ||
                      "Lead"
                    : `#${activeEvent.booking_reference}`}
                </h3>
                <p className="mt-0.5 text-xs text-ink-tertiary">
                  {formatTime(
                    activeEvent.scheduled_time ||
                      activeEvent.event_time ||
                      activeEvent.start_time,
                  )}
                  {activeEvent.status_label || getStatusLabel(activeEvent.status)
                    ? ` · ${activeEvent.status_label || getStatusLabel(activeEvent.status)}`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveEvent(null)}
                className="rounded-md p-1.5 text-ink-tertiary hover:bg-surface-muted"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto px-4 py-4">
              {activeEvent.kind === "lead" && (
                <>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={activeEvent.pinned ? "secondary" : "outline"}
                      className="rounded-md"
                      loading={pinMutation.isPending}
                      onClick={() =>
                        pinMutation.mutate({
                          event: activeEvent,
                          pinned: !activeEvent.pinned,
                        })
                      }
                    >
                      {activeEvent.pinned ? (
                        <>
                          <PinOff className="mr-1.5 h-3.5 w-3.5" />
                          Unpin
                        </>
                      ) : (
                        <>
                          <Pin className="mr-1.5 h-3.5 w-3.5" />
                          Pin
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-md"
                      onClick={() => openFullPage(activeEvent)}
                    >
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                      Open lead
                    </Button>
                  </div>

                  {activeEvent.vendor_notes ? (
                    <div className="rounded-lg border border-border bg-surface-muted/60 p-3">
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
                        Notes
                      </p>
                      <p className="whitespace-pre-wrap text-xs text-ink leading-relaxed">
                        {activeEvent.vendor_notes}
                      </p>
                    </div>
                  ) : null}

                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-ink">
                      <StickyNote className="h-3.5 w-3.5" />
                      Add note
                    </label>
                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      rows={3}
                      placeholder="Quick follow-up note…"
                      className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-xs text-ink focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="mt-2 rounded-md"
                      disabled={!noteDraft.trim()}
                      loading={noteMutation.isPending}
                      onClick={() =>
                        noteMutation.mutate({
                          event: activeEvent,
                          note: noteDraft.trim(),
                        })
                      }
                    >
                      Save note
                    </Button>
                  </div>
                </>
              )}

              {activeEvent.kind === "booking" && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="rounded-md"
                  onClick={() => openFullPage(activeEvent)}
                >
                  Open booking
                </Button>
              )}

              <p className="text-[11px] text-ink-tertiary leading-relaxed">
                Drag items onto another day to reschedule. Booked leads stay locked.
              </p>
            </div>
          </div>
        )}
      </ModalShell>
    </div>
  );
};

export default VendorCalendar;
